import { Appointment, HealthAssessment, MedicalCertificate, SystemConfig } from '../types';
import { isHealthSurveillanceName } from './appointmentServices';
import { isDrugAndAlcoholName } from './certificateResults';
import { formatUkDate } from './dates';

const FALLBACK_LOGO_URL = 'https://placehold.co/150x50/009FE3/ffffff?text=K2+OH';
const FALLBACK_SIGNATURE_URL = 'https://placehold.co/150x50/white/000?text=Signed';

const escapeHtml = (value: unknown) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const isHttpLikeUrl = (value: string) => /^(https?:|data:image\/)/i.test(value);

// Certificates are legal medical documents rendered from an admin-editable
// template, then shown to the patient in a print window / PDF capture. The
// template must never yield an interactive document: no scripts, no
// contenteditable regions, no live form controls (a patient could otherwise
// alter their outcome before printing). Applied to every render regardless of
// what the stored template contains.
const stripInteractiveTagAttributes = (tag: string) =>
  tag
    .replace(/\scontenteditable\s*=\s*("[^"]*"|'[^']*'|[^\s>]*)/gi, '')
    .replace(/\scontenteditable(?=[\s/>])/gi, '')
    .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]*)/gi, '');

export const sanitizeCertificateHtml = (html: string) =>
  String(html || '')
    .replace(/<script\b[\s\S]*?<\/script\s*>/gi, '')
    .replace(/<script\b[^>]*>/gi, '')
    .replace(/<[a-zA-Z][^>]*>/g, stripInteractiveTagAttributes)
    .replace(/<(input|textarea|select|button)\b/gi, '<$1 disabled');

const sanitizeImageUrl = (value: unknown, fallback: string) => {
  const raw = String(value ?? '').trim();
  if (!raw) return fallback;
  if (!isHttpLikeUrl(raw)) return fallback;
  return raw;
};

export const formatCertificateDate = (value?: string | null, fallback = 'N/A') => {
  const raw = String(value || '').trim();
  if (!raw) return fallback;
  return formatUkDate(raw, raw || fallback);
};

export const buildCertificateVerificationUrl = ({
  systemConfig,
  certificateId
}: {
  systemConfig: SystemConfig;
  certificateId: string;
}) => {
  const baseUrl = (
    systemConfig.verification?.urlOverride ||
    systemConfig.general.appUrl ||
    window.location.origin
  ).replace(/\/$/, '');

  return `${baseUrl}?mode=verify&certId=${encodeURIComponent(certificateId)}`;
};

const replaceImagePlaceholder = ({
  html,
  token,
  imageUrl,
  alt,
  style
}: {
  html: string;
  token: string;
  imageUrl: string;
  alt: string;
  style: string;
}) => {
  const escapedUrl = escapeHtml(imageUrl);
  const srcPlaceholderPattern = new RegExp(`src=(["'])\\{${token}\\}\\1`, 'g');
  const standalonePattern = new RegExp(`\\{${token}\\}`, 'g');

  return html
    .replace(srcPlaceholderPattern, () => `src="${escapedUrl}"`)
    .replace(
      standalonePattern,
      () => `<img src="${escapedUrl}" alt="${escapeHtml(alt)}" style="${escapeHtml(style)}" />`
    );
};

// The certificate stores restrictions as a single scalar string, but the technician
// selects them as predefined clauses (a string[]) in the Health Assessment when they
// mark the patient "Fit with Restrictions". Derive the certificate's restrictions text
// from those clauses so the selection actually reaches the generated certificate, and
// append any additional free-text restrictions the technician typed on the certificate
// panel. When no clauses are selected, the free text is used on its own.
export const resolveCertificateRestrictions = (
  healthAssessment: HealthAssessment | undefined,
  freeTextRestrictions?: string | null
): string | undefined => {
  const clauses =
    healthAssessment?.fitStatus === 'fit_restrictions'
      ? (healthAssessment.restrictions || []).map((r) => String(r || '').trim()).filter(Boolean)
      : [];

  const freeText = String(freeTextRestrictions ?? '').trim();
  const parts = [...clauses, ...(freeText ? [freeText] : [])];

  return parts.length > 0 ? parts.join('; ') : undefined;
};

// Restrictions only mean something next to a "Fit with Restrictions"
// determination — either the certificate's conclusion or one of its per-service
// result rows. A certificate that concludes FIT (or REFERRED/UNFIT) must never
// print a Restrictions block: the text is left over from an earlier conclusion
// or from a Health Assessment the technician overrode on the certificate panel,
// and printing it contradicts the conclusion the employer reads.
export const certificateHasRestrictionOutcome = (certificate: {
  outcome?: string | null;
  results?: ({ outcome?: string | null } | null)[] | null;
}): boolean =>
  certificate.outcome === 'fit_restrictions' ||
  (certificate.results || []).some((row) => row?.outcome === 'fit_restrictions');

export const buildCertificateHtml = ({
  template,
  systemConfig,
  appointment,
  certificate,
  clientName
}: {
  template: string;
  systemConfig: SystemConfig;
  appointment: Appointment;
  certificate: MedicalCertificate;
  clientName: string;
}) => {
  const verificationUrl = buildCertificateVerificationUrl({
    systemConfig,
    certificateId: certificate.id
  });
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(verificationUrl)}`;

  const logoUrl = sanitizeImageUrl(systemConfig.general.logoUrl, FALLBACK_LOGO_URL);
  const signatureUrl = sanitizeImageUrl(certificate.technicianSignature, FALLBACK_SIGNATURE_URL);
  // Prefer the job title captured on the certificate (confirmed/edited by the
  // technician at issue time); fall back to the appointment snapshot for
  // certificates issued before per-certificate job titles existed.
  const jobTitle =
    String(certificate.jobTitle || '').trim() || String(appointment.customerJobTitle || '').trim();

  let html = String(template || '');
  html = replaceImagePlaceholder({
    html,
    token: 'app_logo',
    imageUrl: logoUrl,
    alt: 'App logo',
    style: 'max-height:56px;width:auto;object-fit:contain;'
  });
  html = replaceImagePlaceholder({
    html,
    token: 'signature',
    imageUrl: signatureUrl,
    alt: 'Technician signature',
    style: 'max-height:60px;width:auto;object-fit:contain;'
  });
  html = replaceImagePlaceholder({
    html,
    token: 'qr_code',
    imageUrl: qrCodeUrl,
    alt: 'Verification QR',
    style: 'width:100px;height:100px;'
  });

  // Per-service/test rows. Names are snapshotted on the certificate at issue
  // time — never re-joined against the live services/add-ons catalogs.
  const OUTCOME_COLUMNS = ['fit', 'fit_restrictions', 'referred', 'unfit'] as const;
  const DA_OUTCOME_LABEL: Record<string, string> = {
    negative: 'NEGATIVE',
    non_negative: 'NON-NEGATIVE',
    not_tested: 'NOT TESTED',
    // "Not Recorded": the test was booked, the technician selected nothing.
    // The line prints, the result stays blank — never a tick, never a verdict.
    not_recorded: ''
  };
  // Own-property check: `in` would also accept prototype keys.
  const isDrugOutcome = (outcome: string) =>
    Object.prototype.hasOwnProperty.call(DA_OUTCOME_LABEL, outcome);
  // A row belongs to the drug screen if its outcome says so OR its snapshotted
  // name does. The name check covers certificates issued while D&A rows could
  // still pick up a fitness verdict from the per-service dropdowns: reprinting
  // one must show a blank result, not the "Fit" tick that got stored.
  const isDrugRow = (row: { name: string; outcome: string }) =>
    isDrugOutcome(row.outcome) || isDrugAndAlcoholName(row.name);
  const drugResultLabel = (row: { outcome: string }) => DA_OUTCOME_LABEL[row.outcome] ?? '';
  const storedResults = Array.isArray(certificate.results) ? certificate.results : [];
  // Certificates issued before per-test results existed only carry the legacy
  // outcome/drug-test fields. Templates print their table exclusively from
  // {results_rows}, so synthesize the equivalent rows for those certs. When the
  // booked service is itself the drug screen (a D&A-only visit) the service row
  // IS the drug row: it takes the drug outcome, or prints blank when none was
  // recorded. Giving it the fitness verdict instead would tick "Fit" for a
  // screening test nobody performed.
  const legacyServiceIsDrugTest = isDrugAndAlcoholName(String(appointment.serviceName || ''));
  const legacyResults: NonNullable<MedicalCertificate['results']> = [
    legacyServiceIsDrugTest
      ? {
          kind: 'service' as const,
          id: appointment.serviceId || 'service',
          name: appointment.serviceName || 'Drugs & Alcohol Test',
          outcome: certificate.drugTestOutcome || 'not_recorded'
        }
      : {
          kind: 'service' as const,
          id: appointment.serviceId || 'service',
          name: appointment.serviceName || 'Occupational Health Assessment',
          outcome: certificate.outcome || 'fit'
        },
    ...(!legacyServiceIsDrugTest && certificate.drugTestOutcome
      ? [
          {
            kind: 'add_on' as const,
            id: 'drug-alcohol-legacy',
            name: 'Drugs & Alcohol Test',
            outcome: certificate.drugTestOutcome
          }
        ]
      : [])
  ];
  const certificateResults = storedResults.length > 0 ? storedResults : legacyResults;
  const anyFitnessRow = certificateResults.some(
    (row) => !isHealthSurveillanceName(row.name) && !isDrugRow(row)
  );
  const withoutShell = anyFitnessRow
    ? certificateResults.filter((row) => !isHealthSurveillanceName(row.name))
    : certificateResults;
  const conclusionRowIndex = withoutShell.findIndex((row) => !isDrugRow(row));
  const printedResults =
    withoutShell.length !== certificateResults.length &&
    conclusionRowIndex !== -1 &&
    certificate.outcome
      ? withoutShell.map((row, index) =>
          index === conclusionRowIndex ? { ...row, outcome: certificate.outcome as string } : row
        )
      : withoutShell;
  // A certificate whose every printed row is a drug-screen result documents a
  // D&A-only visit: no medical examination happened, so the medical framing
  // (fitness tick columns, the assessed-medicals list, "Assessment
  // Conclusion") must give way to a plain screening presentation.
  const isScreeningOnly =
    printedResults.length > 0 && printedResults.every((row) => isDrugRow(row));
  const resultsRowsHtml = printedResults
    .map((row) => {
      const cells = isDrugRow(row)
        ? `<td colspan="4" style="text-align:center">${escapeHtml(drugResultLabel(row))}</td>`
        : OUTCOME_COLUMNS.map(
            (column) =>
              `<td style="text-align:center">${row.outcome === column ? '&#10003;' : ''}</td>`
          ).join('');
      return `<tr><td>${escapeHtml(row.name)}</td>${cells}</tr>`;
    })
    .join('');
  const resultServiceNames = printedResults
    .filter((row) => row.kind === 'service')
    .map((row) => row.name);
  const serviceNamesText =
    resultServiceNames.length > 0
      ? resultServiceNames.join(', ')
      : String(appointment.serviceName || '').trim();
  const assessedForText = printedResults
    .filter((row) => row.kind === 'add_on')
    .map((row) => row.name)
    .join(', ');

  // Screening-only presentation. The templates carry the medical framing as
  // tokens so a D&A-only certificate stops presenting as a Safety Critical
  // medical: the fitness tick headers become a single "Result" header, the
  // assessed-for line lists the drug screen actually performed instead of the
  // six medical assessments, and the conclusion line reports the screening
  // result rather than a fitness verdict.
  const MEDICAL_RESULTS_HEAD =
    '<tr>' +
    '<th></th>' +
    '<th>Fit</th>' +
    '<th>Fit with<br>Restriction<br><span class="rt-small">(see below)</span></th>' +
    '<th>Referred<br>to OHP</th>' +
    '<th>Unfit<br><span class="rt-small">(Does <strong>not</strong> meet<br>medical standards)</span></th>' +
    '</tr>';
  const SCREENING_RESULTS_HEAD = '<tr><th></th><th colspan="4">Result</th></tr>';
  const MEDICAL_ASSESSED_NAMES =
    'Audiometry, Vision, Blood Pressure, Musculoskeletal, Mental Health, General Health';
  // The battery above is what a medical (SCW-style) examination covers, and it
  // used to print on every non-screening certificate — claiming six
  // assessments nobody performed on a health-surveillance-only visit, and
  // never naming the surveillance tests that WERE performed. Print it only
  // when a medical row is on the certificate, followed by the booked tests.
  const isMedicalRow = (row: { kind: string; name: string; outcome: string }) =>
    row.kind === 'service' && !isHealthSurveillanceName(row.name) && !isDrugRow(row);
  const testRowNames = printedResults
    .filter((row) => !isDrugRow(row) && !isMedicalRow(row))
    .map((row) => row.name);
  const medicalAssessedNames =
    (printedResults.some(isMedicalRow)
      ? [MEDICAL_ASSESSED_NAMES, ...testRowNames]
      : testRowNames
    ).join(', ') || MEDICAL_ASSESSED_NAMES;
  const drugRows = printedResults.filter((row) => isDrugRow(row));
  const screeningAssessedNames = drugRows.map((row) => row.name).join(', ');
  // Severity-ordered so a mixed set never over-claims a clean screen. Rows the
  // technician left blank contribute nothing — with no recorded result at all
  // the certificate states no screening conclusion.
  const recordedDrugRows = drugRows.filter((row) => Boolean(drugResultLabel(row)));
  const screeningConclusion =
    recordedDrugRows.length === 0
      ? ''
      : recordedDrugRows.some((row) => row.outcome === 'non_negative')
        ? DA_OUTCOME_LABEL.non_negative
        : recordedDrugRows.some((row) => row.outcome === 'not_tested')
          ? DA_OUTCOME_LABEL.not_tested
          : DA_OUTCOME_LABEL.negative;
  const outcomeText = String(certificate.outcome || 'N/A')
    .replace(/_/g, ' ')
    .toUpperCase();
  // A screening visit still records a mandatory fitness determination; when it
  // is anything other than plain "fit" (restrictions, referred, unfit) the
  // verdict must stay on the certificate alongside the screening result.
  const fitnessIsNotable = Boolean(certificate.outcome) && certificate.outcome !== 'fit';
  const screeningLine = screeningConclusion
    ? fitnessIsNotable
      ? `Screening Result: ${screeningConclusion} — Assessment Conclusion: ${outcomeText}`
      : `Screening Result: ${screeningConclusion}`
    : fitnessIsNotable
      ? `Assessment Conclusion: ${outcomeText}`
      : '';
  const conclusionLine = isScreeningOnly ? screeningLine : `Assessment Conclusion: ${outcomeText}`;

  const replacements: Record<string, string> = {
    service_names: escapeHtml(serviceNamesText),
    results_rows: resultsRowsHtml,
    results_head: isScreeningOnly ? SCREENING_RESULTS_HEAD : MEDICAL_RESULTS_HEAD,
    assessed_for_names: escapeHtml(
      isScreeningOnly
        ? screeningAssessedNames || 'Drug and Alcohol Screening'
        : medicalAssessedNames
    ),
    conclusion_line: escapeHtml(conclusionLine),
    assessed_for: escapeHtml(assessedForText),
    name: escapeHtml(appointment.customerName || 'N/A'),
    dob: escapeHtml(formatCertificateDate(appointment.customerDob, 'N/A')),
    job_title: escapeHtml(jobTitle || 'N/A'),
    client_name: escapeHtml(clientName || 'N/A'),
    date_of_medical: escapeHtml(formatCertificateDate(certificate.issuedAt)),
    expiry_date: escapeHtml(formatCertificateDate(certificate.validUntil)),
    technician_name: escapeHtml(certificate.technicianName || 'N/A'),
    outcome: escapeHtml(
      String(certificate.outcome || 'N/A')
        .replace(/_/g, ' ')
        .toUpperCase()
    ),
    drug_outcome: escapeHtml(
      String(certificate.drugTestOutcome || 'N/A')
        .replace(/_/g, ' ')
        .toUpperCase()
    ),
    advice: escapeHtml(certificate.advice || 'None'),
    restrictions:
      certificate.restrictions && certificateHasRestrictionOutcome(certificate)
        ? `<p><strong>Restrictions:</strong> ${escapeHtml(certificate.restrictions)}</p>`
        : '',
    // Prefer snapshotted test names from the results; raw ids only for legacy
    // certificates issued before per-test results existed.
    addons_list: escapeHtml(assessedForText || (appointment.addOnIds || []).join(', ') || 'None'),
    ref: escapeHtml(certificate.id || '')
  };

  // Single pass with a function replacement: user-derived values (names,
  // advice, restrictions) can contain `$&`-style metacharacters or text that
  // looks like a token — a per-token replace() loop would interpret the
  // former and re-scan the latter, corrupting the printed certificate.
  const tokenPattern = new RegExp(`\\{(${Object.keys(replacements).join('|')})\\}`, 'g');
  html = html.replace(tokenPattern, (match, token: string) => replacements[token] ?? match);

  return sanitizeCertificateHtml(html);
};

const toFileSafeSegment = (value: string, fallback: string) => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return normalized || fallback;
};

const formatFileDate = (value?: string | null) => {
  const raw = String(value || '').trim();
  if (!raw) return 'date-unknown';
  const prefixMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (prefixMatch) return `${prefixMatch[1]}-${prefixMatch[2]}-${prefixMatch[3]}`;
  return toFileSafeSegment(formatCertificateDate(raw, 'date-unknown'), 'date-unknown');
};

export const resolveCertificateClientName = (
  appointment: Appointment,
  companyNameFallback?: string
) => {
  const appointmentCompanyName = String(appointment.companyName || '').trim();
  if (appointmentCompanyName) return appointmentCompanyName;

  const fallback = String(companyNameFallback || '').trim();
  if (fallback) return fallback;

  if (appointment.companyId) return 'Corporate Client';
  return 'Individual';
};

export const buildCertificateFileName = ({
  appointment,
  certificate
}: {
  appointment: Appointment;
  certificate: MedicalCertificate;
}) => {
  const idPart = toFileSafeSegment(certificate.id, 'certificate');
  const namePart = toFileSafeSegment(appointment.customerName || 'patient', 'patient');
  const datePart = formatFileDate(certificate.issuedAt || appointment.date);
  return `certificate-${idPart}-${namePart}-${datePart}`;
};

// Browsers omit CSS background images/colors when printing unless the page
// opts into exact color adjustment ("Background graphics" is unticked by
// default in the print dialog). The certificate template draws the outcome and
// drug-test ticks as background images, so without this a certificate that
// looks correct on screen prints — or saves to PDF from the print dialog —
// with the ticks silently missing.
const PRINT_BACKGROUNDS_STYLE =
  '<style>* { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }</style>';

export const forcePrintedBackgrounds = (html: string): string => {
  const headClose = /<\/head\s*>/i.exec(html);
  if (headClose) {
    return html.slice(0, headClose.index) + PRINT_BACKGROUNDS_STYLE + html.slice(headClose.index);
  }
  return PRINT_BACKGROUNDS_STYLE + html;
};

export const openBlankPrintWindow = (): Window | null =>
  window.open('', '', 'width=800,height=900');

export const writeCertificatePrintWindow = (
  html: string,
  suggestedFileName?: string,
  existingWindow?: Window | null
) => {
  if (!html) {
    existingWindow?.close();
    return false;
  }
  const printWindow = existingWindow ?? window.open('', '', 'width=800,height=900');
  if (!printWindow) return false;

  printWindow.document.write(forcePrintedBackgrounds(html));
  printWindow.document.close();
  const title = String(suggestedFileName || 'certificate').trim() || 'certificate';
  printWindow.document.title = title;
  setTimeout(() => printWindow.print(), 500);
  return true;
};

export const openCertificatePrintWindow = (html: string, suggestedFileName?: string) =>
  writeCertificatePrintWindow(html, suggestedFileName);

export const canPrintCertificate = (certificate?: MedicalCertificate | null): boolean => {
  if (!certificate) return false;
  return (certificate.status ?? 'active') === 'active';
};
